import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import PropTypes from "prop-types";
import * as React from "react";

import { StyledTableHead } from "../../table/BaseTable/BaseTable";

import { Typography } from "@mui/material";
import {
    CLOSED_ROW_COLOR,
    FAVORITE_ROW_COLOR,
    NEW_LEAD_COLOR,
    SELECTED_ROW_COLOR,
    NOT_CONNECTED_ROW_COLOR,

} from "../../../helpers/constants";
import { convertBoolenValueToZero } from "../../../helpers/methods";
import WindowService from "../../../services/windowService";
import NoRecordsTableFooter from "../../footer/NoRecordsTableFooter";
import BottomTable from "../../other/BottomTable";
import UniversalTableHeaders from "../UniversalTable/Headers/UniversalTableHeaders";
import FilterTableDataRow from "./FilterTableDataRow";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import {TableRow, TableCell} from "@mui/material";

/**
 * Table component with filtering, selection, pagination and export functionality.
 *
 * Consider using FilterTableWrapper with a more type-safe TS interface and
 * local search params and pagination management before shooting yourself in the foot.
 */
const FilterTable = React.memo((props) => {
    const handleClickCheck = React.useCallback(
        (id) => {
            props.setDataLocal((prevData) => {
                const selectedRow = {
                    ...prevData.find((rowData) => rowData.id === id),
                };
                selectedRow.selected = !selectedRow.selected;
                return prevData.map((rowData) => {
                    if (rowData.id === id) {
                        return selectedRow;
                    }
                    return rowData;
                });
            });
            if (props.onClickCheck) {
                props.onClickCheck(id)
            }
        },
        [props.setDataLocal, props.onClickCheck],
    );

    const handleCheckAll = React.useCallback(() => {
        props.setDataLocal((prev) => {
            const hasSelectedRows = prev?.some((rowData) => rowData.selected);
            const shouldSelectAll = !hasSelectedRows;

            props.setCheckedAll(shouldSelectAll);

            return prev?.map((rowData) => ({
                ...rowData,
                selected: rowData?.transfer_is_allowed_to_book == false ? false : shouldSelectAll,
            }));
        });
    }, [props.setCheckedAll, props.setDataLocal]);

    const handleClickRow = React.useCallback(
        (id) => {
            if (!id || id === props.selectedItemId) {
                props.setSelectedRowId(undefined);
            } else {
                props.setSelectedRowId(id);
            }
        },
        [props.setSelectedRowId, props.selectedItemId],
    );

    const getRowBackgroundColor = (props, rowData) => {
        if (props.disableRowHighlight) return undefined

        if (rowData.leadStatusHighlight || rowData.transfer_is_booked || rowData.transfer_is_invoiced) {
            return NEW_LEAD_COLOR;
        }

        if (rowData.invoice_is_connected === false) {
            return NOT_CONNECTED_ROW_COLOR
        }

        return props.selectedItemId === rowData.id
            ? SELECTED_ROW_COLOR
            : rowData.is_favorite === true
              ? FAVORITE_ROW_COLOR
              : (!rowData.is_closed && rowData.is_claim === true) || rowData.is_exceed
                ? CLOSED_ROW_COLOR
                : undefined;
    };

    const runDetailsMethod = React.useCallback(
        (rowData) => {
            const idToRunDetailsMethod = props.dataKeyToRunDetailsMethod
                ? rowData.data.find(
                      (tableField) => tableField.name === props.dataKeyToRunDetailsMethod,
                  )?.value
                : rowData.id;
            if (props.onClickDetailsIcon) {
                props.onClickDetailsIcon(idToRunDetailsMethod);
            } else {
                WindowService.openInNewTab(
                    `/${props.itemDetailsLinkPrefix}/${idToRunDetailsMethod}/`,
                );
            }
        },
        [props.onClickDetailsIcon, props.dataKeyToRunDetailsMethod, props.itemDetailsLinkPrefix],
    );

    return (
        <>
            <TableContainer component={Paper} style={props.style}>
                {props.showTitle && props.title && (
                    <Typography variant="h6" color="primary">
                        {props.title}
                    </Typography>
                )}
                <Table stickyHeader>
                    <StyledTableHead
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <UniversalTableHeaders
                            showDetailsIcon={props.showDetailsIcon}
                            showChangesCircle={props.showChangesCircle}
                            hideFiltersIconCol={props.hideFiltersIconCol}
                            showFilters={props.showFilters}
                            initialParams={props.initialParams}
                            // showFilters={false}
                            showCheckbox={props.showCheckbox}
                            checkedAll={props.checkedAll}
                            onClickCheckAll={handleCheckAll}
                            headersConfig={props.headersConfig}
                            resetPageNumber={props.resetPageNumber}
                            filterPrefix={props.filterPrefix}
                            showCleanFilterIcon={props.showCleanFilterIcon}
                            initialExpanded={props.initialExpandedFilterHeader}
                            showChangesCircleFilter={props.showChangesCircleFilter}
                            excludedKeysForClearSearchParams={
                                props.excludedKeysForClearSearchParams
                            }
                            onSearchCb={props.onSearchCb}
                            alwaysExpanded={props.alwaysExpandedFilters}
                            sortParams={props.sortParams}
                            handleSort={props.handleSort}
                            disableFilterCheckBox={props.disableFilterCheckBox}
                        />
                    </StyledTableHead>
                    <TableBody>
                        {props.isLoading && 
                        <TableRow >
                            <TableCell colspan={
                                convertBoolenValueToZero(props.showCheckbox) +
                                convertBoolenValueToZero(props.showChangesCircle) +
                                convertBoolenValueToZero(props.showDetailsIcon) +
                                convertBoolenValueToZero(props.showFilters) +
                                props.headersConfig.length
                            }>
                                <LoaderWrapper showLoader={true}/>
                            </TableCell>
                        </TableRow>}
                        {!props.isLoading && props.data.map((rowData, index) => (
                            <FilterTableDataRow
                                hasChanges={rowData.hasChanges}
                                showChangesCircle={props.showChangesCircle}
                                showFilters={!props.hideFiltersIconCol}
                                showCheckbox={props.showCheckbox}
                                showDetailsIcon={props.showDetailsIcon}
                                onClickDetailsIcon={() => runDetailsMethod(rowData)}
                                key={rowData.id}
                                id={rowData.id}
                                updated={rowData.updated}
                                backgroundColor={getRowBackgroundColor(props, rowData)}
                                selected={rowData.selected}
                                checkboxIsDisabled={rowData.checkboxIsDisabled}
                                data={rowData.data}
                                showContextMenu={props.showContextMenu}
                                contextMenuActions={props.contextMenuActions}
                                draggableRow={props.draggableRows}
                                onDragEnd={props.onDragEndRow}
                                onDragStart={props.onDragStartRow}
                                detailsTitle={props.detailsTitle}
                                onClickRow={handleClickRow}
                                onClickCheck={handleClickCheck}
                                disableRowHighlight={props.disableRowHighlight}
                                // hideFiltersIconCol={props.hideFiltersIconCol}
                            />
                        ))}
                    </TableBody>

                    {!props.isLoading && props.data.length === 0 && (
                        <NoRecordsTableFooter
                            colspan={
                                convertBoolenValueToZero(props.showCheckbox) +
                                convertBoolenValueToZero(props.showChangesCircle) +
                                convertBoolenValueToZero(props.showDetailsIcon) +
                                convertBoolenValueToZero(props.showFilters) +
                                props.headersConfig.length
                            }
                        />
                    )}
                </Table>
            </TableContainer>
            <BottomTable
                showExportToFileButton={props.showExportToFileButton}
                showCounterRecords={props.showCounterRecords}
                withPagination={props.withPagination}
                page={props.page}
                pageKey={props.pageKey}
                countRecords={props.countRecords ? props.countRecords : props.data.length}
                onPageChange={props.onPageChange}
                rowsPerPage={props.rowsPerPage}
                onRowsPerPageChange={props.onRowsPerPageChange}
                rowsPerPageOptions={props.rowsPerPageOptions}
                exportToFileUrl={props.exportToFileUrl}
                exportToFileSearchParams={props.exportToFileSearchParams}
                exportToFileHeaders={props.exportToFileHeaders}
                exportToFileFileName={props.exportToFileFileName}
                exportToFileRequestContent={props.exportToFileRequestContent}
                filterPrefix={props.filterPrefix}
                isLoading={props.isLoading}
            />
        </>
    );
});

export default FilterTable;

FilterTable.propTypes = {
    showCheckbox: PropTypes.bool,
    showDetailsIcon: PropTypes.bool,
    itemDetailsLinkPrefix: PropTypes.string,
    checkedAll: PropTypes.bool,
    onClickCheckAll: PropTypes.func,
    onClickCheck: PropTypes.func,
    onClickRow: PropTypes.func,
    data: PropTypes.array,
    headersConfig: PropTypes.object,
    filteringData: PropTypes.object,
    countRecords: PropTypes.number,
    page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pageKey: PropTypes.string,
    onPageChange: PropTypes.func,
    rowsPerPage: PropTypes.number,
    onRowsPerPageChange: PropTypes.func,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    resetPageNumber: PropTypes.func,
    CollapsibleElement: PropTypes.element,
    selectedItemId: PropTypes.string,
    style: PropTypes.object,
    showTitle: PropTypes.bool,
    title: PropTypes.string,
    filterPrefix: PropTypes.string,
    showCleanFilterIcon: PropTypes.bool,
    showFilters: PropTypes.bool,
    initialExpandedFilterHeader: PropTypes.bool,
    withPagination: PropTypes.bool,
    showContextMenu: PropTypes.bool,
    contextMenuActions: PropTypes.arrayOf(PropTypes.object),
    draggableRows: PropTypes.bool,
    onDragEndRow: PropTypes.func,
    onDragStartRow: PropTypes.func,
    showChangesCircle: PropTypes.bool,
    showChangesCircleFilter: PropTypes.bool,
    hasChanges: PropTypes.bool,
    showExportToFileButton: PropTypes.bool,
    exportToFileUrl: PropTypes.string,
    exportToFileSearchParams: PropTypes.object,
    exportToFileHeaders: PropTypes.array,
    exportToFileFileName: PropTypes.string,
    showCounterRecords: PropTypes.bool,
    exportToFileRequestContent: PropTypes.object,
    excludedKeysForClearSearchParams: PropTypes.array,
    dataKeyToRunDetailsMethod: PropTypes.string,
    showFavorite: PropTypes.bool,
    isLoading: PropTypes.bool,
    disableRowHighlight: PropTypes.bool,
};

FilterTable.defaultProps = {
    showDetailsIcon: false,
    showCheckbox: true,
    showTitle: false,
    title: "",
    showCleanFilterIcon: false,
    showFilters: true,
    initialExpandedFilterHeader: false,
    onClickRow: () => {},
    showContextMenu: false,
    contextMenuActions: [],
    withPagination: true,
    draggableRows: false,
    showPagination: true,
    showChangesCircle: false,
    showChangesCircleFilter: true,
    hasChanges: true,
    showCounterRecords: true,
    showFavorite: true,
    setSelectedRowId: () => {},
    disableRowHighlight: false,
};
